import { useMemo } from 'react';
import { useFormikContext } from 'formik';

import {
  Registry,
  RegistryId,
} from '@/react/portainer/registries/types/registry';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { AutocompleteSelect } from '@@/form-components/AutocompleteSelect';
import { TextTip } from '@@/Tip/TextTip';
import { Link } from '@@/Link';

import {
  listableRegistries,
  useAddonRegistries,
} from '../queries/useAddonRegistries';
import { AddonChartSourceStatus } from '../queries/useAddonChartSourceStatus';
import { useAddonRegistryRepositories } from '../queries/useAddonRegistryRepositories';
import { useAddonRepositoryKind } from '../queries/useAddonRepositoryKind';
import { useAddonEnvironmentId } from '../useAddonEnvironmentId';

import { InstallFormValues } from './types';
import { matchingRepositories } from './repositorySuggestions';
import { AddonChartSource } from './useAddonChartSource';
import { registryLabel } from './registryLabel';
import { RepositoryKindMismatch } from './RepositoryKindMismatch';
import { ChartSourceStatus } from './ChartSourceStatus';

/** Registry id standing for the add-on's default, public chart source. */
const DEFAULT_SOURCE_REGISTRY_ID = 0;

/**
 * Chart-source fields for the install wizard: which registry the add-on's Helm
 * chart is pulled from, and which chart in it. The add-on's own public chart is
 * offered as the default, so an install needs no registry set up.
 *
 * The source is fixed once the add-on is installed — re-pointing a running release
 * at another registry is not supported — so when read-only this shows the source
 * the release was installed from instead of the selects.
 */
export function ChartSourceFormSection({
  source,
  status,
  isStatusLoading,
  isLoading,
  readOnly,
}: {
  source: AddonChartSource;
  // What a live check of the chosen source found. Shown with the fields that
  // choose it, so an unreadable location is answered where it is set rather than
  // inferred from an empty version list.
  status?: AddonChartSourceStatus;
  isStatusLoading?: boolean;
  isLoading?: boolean;
  readOnly?: boolean;
}) {
  const { setFieldValue } = useFormikContext<InstallFormValues>();
  const environmentId = useAddonEnvironmentId().data;
  // A chart is searched from the registry, so only registries that serve a catalog
  // can be offered as a source.
  const registriesQuery = useAddonRegistries(environmentId, {
    select: listableRegistries,
  });
  const registries = registriesQuery.data ?? [];

  if (readOnly) {
    return (
      <InstalledChartSource
        source={source}
        status={status}
        isStatusLoading={isStatusLoading}
        // isInitialLoading, not isLoading: the registries query is disabled until
        // the environment resolves, and a disabled query reports isLoading forever.
        isLoading={isLoading || registriesQuery.isInitialLoading}
        registries={registries}
      />
    );
  }

  return (
    <>
      <FormControl
        label="Chart source"
        tooltip={<ChartSourceTooltip />}
        size="vertical"
        inputId="addon-chart-source"
        errors={registriesQuery.isError && 'Unable to load registries.'}
      >
        <PortainerSelect<RegistryId>
          inputId="addon-chart-source"
          data-cy="addon-install-chart-source"
          value={source.registryId ?? DEFAULT_SOURCE_REGISTRY_ID}
          options={[
            {
              // The catalog names the default chart. Without it there is nothing
              // to name — the catalog could not be read — and the option stays,
              // so what is selected is still visible while a registry is picked.
              label: source.catalogChart
                ? `${source.catalogChart} (default)`
                : 'No chart from the catalog (default)',
              value: DEFAULT_SOURCE_REGISTRY_ID,
            },
            ...registries.map((registry) => ({
              label: registryLabel(registry.URL, registry.Name),
              value: registry.Id,
            })),
          ]}
          isLoading={registriesQuery.isInitialLoading}
          onChange={handleRegistryChange}
        />
        {/* Inside the field rather than after it: the status belongs to the
            source these fields choose, and a sibling of the control would sit in
            the margin between this field and the next, reading as a note on
            neither. */}
        <ChartSourceStatus
          status={status}
          isLoading={isStatusLoading}
          canChangeSource
          // A registry's own skip-verify setting is the administrator's, made
          // for that registry; only the catalog's default is worth reporting.
          warnUnverified={!source.registryId}
        />
      </FormControl>

      {!!source.registryId && (
        <RegistryChartSearch
          registryId={source.registryId}
          environmentId={environmentId}
          value={source.chart}
          onChange={handleChartChange}
          onSelect={handleChartSelect}
        />
      )}
    </>
  );

  // Changing the source invalidates the chart and version picked under the old
  // one, so both are cleared. The default source needs no chart: the catalog's
  // reference is the whole chart path.
  //
  // The image registry follows the chart's, because an air-gapped install
  // mirrors both into the same private registry and the image half lives inside
  // Advanced configuration, folded away. Left to default to "chart default" it
  // is the step nothing on the visible form asks for, and the release installs
  // to pods stuck in ImagePullBackOff with no pull secret. Still editable: an
  // image mirrored elsewhere is picked there as before.
  function handleRegistryChange(registryId: RegistryId | null) {
    setFieldValue('registryId', registryId || undefined);
    setFieldValue('chart', '');
    setFieldValue('chartConfirmed', '');
    setFieldValue('version', '');
    setFieldValue('imageRegistryId', registryId || undefined);
    setFieldValue('imagePath', '');
    setFieldValue('values.imageTag', '');
  }

  // Trimmed as it is entered, not on submit: a path pasted with surrounding
  // whitespace is searched, version-listed and source-checked long before it is
  // installed, and each of those would miss for a reason nothing on screen
  // explains.
  function handleChartChange(chart: string) {
    setFieldValue('chart', chart.trim());
    setFieldValue('version', '');
  }

  // Arms the live reachability check: it only reruns once the chart is settled
  // — picked from the registry's suggestions, or left after typing — not on
  // every keystroke of a path still being typed or pasted.
  function handleChartSelect(chart: string) {
    setFieldValue('chartConfirmed', chart.trim());
  }
}

// Searches the registry's repositories for the chart to install. A search box
// rather than a dropdown: the registry's catalog does not record which
// repositories are Helm charts, and some registries cannot list them at all, so
// any path must remain enterable.
function RegistryChartSearch({
  registryId,
  environmentId,
  value,
  onChange,
  onSelect,
}: {
  registryId: RegistryId;
  environmentId?: EnvironmentId;
  value: string;
  onChange: (chart: string) => void;
  onSelect: (chart: string) => void;
}) {
  const repositoriesQuery = useAddonRegistryRepositories(
    registryId,
    environmentId
  );
  const repositories = repositoriesQuery.data;
  const kindQuery = useAddonRepositoryKind({
    registryId,
    repository: value,
    environmentId,
  });

  // Suggestions only once something is typed: a registry can hold hundreds of
  // repositories, and the catalog does not record which of them are Helm charts.
  const searchResults = useMemo(
    () => matchingRepositories(value, repositories),
    [repositories, value]
  );

  return (
    <FormControl
      label="Chart"
      tooltip="Start typing to search the registry's repositories. Any path can be entered, so a repository the registry does not list can still be used."
      size="vertical"
      inputId="addon-chart"
      required
    >
      <AutocompleteSelect
        inputId="addon-chart"
        data-cy="addon-install-chart-input"
        value={value}
        onChange={onChange}
        onSelect={onSelect}
        searchResults={searchResults}
        placeholder="Search or enter a chart path, e.g. charts/portainer-run"
      />
      <RepositoryKindMismatch actual={kindQuery.data} expected="chart" />
      {/* Listing is only there to suggest: a registry that cannot list its
          repositories still leaves the path typeable, so this is not an error. */}
      {repositoriesQuery.isError && (
        <TextTip color="blue" className="mt-2">
          This registry&apos;s repositories could not be listed, so no
          suggestions are available. Enter the chart&apos;s path directly.
        </TextTip>
      )}
    </FormControl>
  );
}

// Shows the source a release was installed from. The registry is named where it
// is still known; a registry removed since the install leaves only the chart path,
// and the add-on's version check reports the broken source separately.
function InstalledChartSource({
  source,
  status,
  isStatusLoading,
  isLoading,
  registries,
}: {
  source: AddonChartSource;
  status?: AddonChartSourceStatus;
  isStatusLoading?: boolean;
  isLoading?: boolean;
  registries: Array<Registry>;
}) {
  const registry = registries.find((r) => r.Id === source.registryId);

  return (
    <FormControl
      label="Chart source"
      size="vertical"
      inputId="addon-chart-source"
      isLoading={isLoading}
      loadingText="Loading chart source..."
    >
      <Input
        id="addon-chart-source"
        value={installedSourceLabel()}
        disabled
        readOnly
        data-cy="addon-install-chart-source"
      />
      <ChartSourceStatus
        status={status}
        isLoading={isStatusLoading}
        warnUnverified={!source.registryId}
      />
    </FormControl>
  );

  function installedSourceLabel() {
    if (!source.registryId) {
      return source.catalogChart;
    }
    if (!registry) {
      return source.chart;
    }

    return `oci://${registry.URL}/${source.chart}`;
  }
}

function ChartSourceTooltip() {
  return (
    <>
      <p>
        Select the <b>registry</b> holding the add-on&apos;s Helm chart, or use
        the add-on&apos;s default public chart.
      </p>
      <p>
        Registries are defined in the{' '}
        <Link
          to="portainer.registries"
          data-cy="registries-view"
          target="_blank"
        >
          registries view
        </Link>
        . Selecting one here also selects it as the image registry under{' '}
        <b>Advanced configuration</b>, so a chart and image mirrored into the
        same private registry both work. Change it there if the image lives
        elsewhere.
      </p>
      <p>The chart source cannot be changed once the add-on is installed.</p>
    </>
  );
}
